/**
 * Device classes (spec section "Device class table"). Pure: the page passes what the browser tells it.
 * Budgets are conservative starting points; the soak tests in the spec adjust them.
 */
import { composeByMemory } from './compose';
import { CAPS, REGISTRY, type Composition } from './registry';

export type DeviceClassName = 'desktop-discrete' | 'desktop-integrated' | 'tablet' | 'phone' | 'phone-weak';
export type CapName = 'full' | 'medium' | 'light';

/** The core's own settings (never traded). */
export interface CoreTier { shadowMapSize: number; cascades: number; hd: boolean; msaa: 0 | 4 }

export interface DeviceClass {
  memBudgetMb: number;
  dprCap: number;
  /** Top mip levels dropped from compressed textures: 0 = 4K on monitors, 1 = 2K on laptops, 2 = 1K on phones. */
  mipSkip: 0 | 1 | 2;
  frameTarget: 30 | 60;
  startCap: CapName;
  core: CoreTier;
}

export const CLASS_TABLE: Record<DeviceClassName, DeviceClass> = {
  'desktop-discrete': { memBudgetMb: 1500, dprCap: 2, mipSkip: 0, frameTarget: 60, startCap: 'full', core: { shadowMapSize: 2048, cascades: 3, hd: true, msaa: 0 } },
  // Integrated (Intel UHD/Iris): a 1024 shadow map. The near cascade re-renders every frame at `shadowMapSize`; at
  // 2048 that is 4.2M shadow fragments per frame (more than a 1600x900 screen) and the CSM is sampled per pixel in
  // the ground and foliage shaders. Measured on an Intel UHD (i7-11850H, 1600x900) light: 1024 took the ride from
  // 14 to 35 fps and the worst hitch from 4.0 s to 64 ms. Two cascades are kept so a capable iGPU that the world fit
  // steps up to `farShadows: far` still gets distant shadows (there `farShadowCascades` uses `cascades`); the `light`
  // composition sets `farShadows: near`, which is one cascade whatever this is. On this iGPU the menu's p90 stays
  // ~55 ms however far the modules step down (the menu is core-bound, not world-bound), so the old `medium` start
  // spent 11 rebuilds and ~115 s before parking at the same `light` floor; starting at `light` settles in two windows.
  // The fit steps up again on the iGPUs where `light` clears the budget, so the weakest is not held there for good.
  'desktop-integrated': { memBudgetMb: 700, dprCap: 2, mipSkip: 1, frameTarget: 30, startCap: 'light', core: { shadowMapSize: 1024, cascades: 2, hd: false, msaa: 0 } },
  tablet: { memBudgetMb: 450, dprCap: 2, mipSkip: 2, frameTarget: 30, startCap: 'light', core: { shadowMapSize: 1024, cascades: 1, hd: false, msaa: 0 } },
  phone: { memBudgetMb: 350, dprCap: 2, mipSkip: 2, frameTarget: 30, startCap: 'light', core: { shadowMapSize: 1024, cascades: 1, hd: false, msaa: 0 } },
  'phone-weak': { memBudgetMb: 250, dprCap: 1.5, mipSkip: 2, frameTarget: 30, startCap: 'light', core: { shadowMapSize: 1024, cascades: 1, hd: false, msaa: 0 } },
};

export interface DeviceFacts {
  renderer: string; webgpu: boolean; mobile: boolean; deviceMemory: number | undefined; screenPixels: number;
  /** The screen's short side in CSS pixels: 600+ is a tablet (Android's sw600dp line), whatever the DPR. */
  shortSide: number; touchPoints: number;
}

const DISCRETE = /nvidia|geforce|rtx|radeon rx|radeon pro|apple m\d|apple gpu|arc a\d/i;
const INTEGRATED = /\bintel\b|uhd graphics|iris/i;
const WEAK_MOBILE = /mali-g[0-4]\d\b|mali-t\d|adreno \(tm\) [1-5]\d\d\b/i; // Mali-G57 (the test tablet) is mid-range, not weak

export function classifyDevice(f: DeviceFacts): DeviceClassName {
  if (f.mobile || f.touchPoints > 1 && f.screenPixels < 1600 * 1200) {
    const weak = WEAK_MOBILE.test(f.renderer) || (f.deviceMemory !== undefined && f.deviceMemory < 4);
    if (weak) return 'phone-weak';
    return f.shortSide >= 600 ? 'tablet' : 'phone';
  }
  if (INTEGRATED.test(f.renderer)) return 'desktop-integrated';
  if (DISCRETE.test(f.renderer) && f.webgpu) return 'desktop-discrete';
  return f.webgpu && !/unknown|swiftshader|llvmpipe|software/i.test(f.renderer) ? 'desktop-discrete' : 'desktop-integrated';
}

/** Memory the core takes (MB): ground sets + models + terrain + shadow maps, by texture size (manifest sizes, 2026-10-03). */
export function coreMemoryMb(mipSkip: 0 | 1 | 2, hd: boolean): number {
  const ground = (hd ? 180 : 90) / 4 ** mipSkip;
  const models = hd ? 120 : 45;
  return Math.ceil(ground + models + 40 /* terrain tiles */ + 32 /* shadow maps */);
}

export const deviceKey = (renderer: string, width: number, height: number, dpr: number): string =>
  `${renderer.trim().toLowerCase()}|${Math.round(width * dpr)}x${Math.round(height * dpr)}`;

/** The composition a Settings choice starts from: that cap (Auto: the class's start cap) under the class's memory budget. */
export const compositionFor = (quality: 'auto' | CapName, dc: DeviceClass, coreMb: number): Composition =>
  composeByMemory(REGISTRY, dc.memBudgetMb, CAPS[quality === 'auto' ? dc.startCap : quality], coreMb);

/** Screen width (CSS px x DPR) from which a desktop streams the 4k ground set: 4K monitors and HiDPI laptops. */
export const GROUND_4K_FROM_PX = 3000;
/**
 * Which KTX2 ground set a desktop streams: 4k only where the screen can show it, 2k elsewhere: a quarter of the bytes
 * (~60 MB a visit instead of ~245) and as sharp on a 1080p/1440p screen. Sharp on screen, not the largest file.
 */
export const groundSet = (screenWidthPx: number): '4k' | '2k' => (screenWidthPx >= GROUND_4K_FROM_PX ? '4k' : '2k');
